import React from "react";
import {
    Badge,
    Grid,
    IconButton,
    Tooltip,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    DateCalendar,
    LocalizationProvider,
    PickersDay,
    PickersDayProps,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Circle } from "@mui/icons-material";
import { NationalHolidaysInterface } from "../../interface/nationalHolidayInterface";
import { Holiday } from "../../interface/holidayInterface";
import "./calendar.css";
import updateLocale from "dayjs/plugin/updateLocale";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

interface MonthDataParams {
    confirming_id: number;
    created_at: string;
    from_date: string;
    id: number;
    notes: string;
    requesting_id: number;
    status: number;
    to_date: string;
    type: number;
}

type SlotProps = {
    day: {
        highlightedDays?: Holiday[];
        nationalHolidays: NationalHolidaysInterface[];
    };
};

dayjs.extend(updateLocale);

dayjs.updateLocale("en", {
    weekStart: 1,
});

const MonthCalendarCustom = ({
    monthData,
    nationalHolidays,
}: {
    monthData: MonthDataParams[];
    nationalHolidays: NationalHolidaysInterface[];
}) => {
    const [year, setYear] = React.useState(new Date().getFullYear());
    const [monthsData, setMonthsData] = React.useState(
        Array.from(Array(12), (_, i) => {
            const month = i + 1; // add 1 to get the correct month number
            const initDate = dayjs(`${year}-${month}-01`);
            return {
                month,
                year,
                initDate,
                monthName: initDate.format("MMM"), // get abbreviated month name
            };
        }),
    );

    const theme = useTheme();

    const nextYear = () => {
        const nextYearValue = year + 1;
        setYear(nextYearValue);
        const newMonthsData = Array.from(Array(12), (_, i) => {
            const month = i + 1; // add 1 to get the correct month number
            const initDate = dayjs(`${nextYearValue}-${month}-01`);
            return {
                month,
                year: nextYearValue,
                initDate,
                monthName: initDate.format("MMM"), // get abbreviated month name
            };
        });
        setMonthsData(newMonthsData);
    };

    const previousYear = () => {
        const prevYearValue = year - 1;
        setYear(prevYearValue);
        const newMonthsData = Array.from(Array(12), (_, i) => {
            const month = i + 1; // add 1 to get the correct month number
            const initDate = dayjs(`${prevYearValue}-${month}-01`);
            return {
                month,
                year: prevYearValue,
                initDate,
                monthName: initDate.format("MMM"), // get abbreviated month name
            };
        });
        setMonthsData(newMonthsData);
    };

    const highlightedDays = monthData;

    interface HolidaysProps extends PickersDayProps<Dayjs> {
        highlightedDays?: Holiday[];
        nationalHolidays: NationalHolidaysInterface[];
    }
    const Holidays: React.FC<HolidaysProps> = (
        props: PickersDayProps<Dayjs> & {
            highlightedDays?: Holiday[];
            nationalHolidays: NationalHolidaysInterface[];
        },
    ) => {
        const {
            highlightedDays = [],
            nationalHolidays = [],
            day,
            outsideCurrentMonth,
            ...other
        } = props;

        const currentMonth = day?.month();

        const isDayOutsideCurrentMonth = day.month() !== currentMonth;

        if (outsideCurrentMonth || isDayOutsideCurrentMonth) {
            return (
                <PickersDay
                    outsideCurrentMonth={outsideCurrentMonth}
                    {...other}
                    day={day}
                />
            );
        }

        const isNationalHoliday = (date: string) => {
            const holiday = nationalHolidays.find((h) => h.date === date);
            return holiday !== undefined;
        };

        const getHolidayTitle = (date: string) => {
            const holiday = nationalHolidays.find((h) => h.date === date);
            return holiday?.title;
        };
        const highlightedHoliday = [...highlightedDays]
            .reverse()
            .find((days) => {
                const fromDate = new Date(days.from_date).setHours(0, 0, 0, 0);
                const startDate = day.toDate().setHours(0, 0, 0, 0);

                return (
                    fromDate <= startDate &&
                    startDate <= new Date(days.to_date).setHours(0, 0, 0, 0)
                );
            });

        let badgeColor;

        if (isNationalHoliday(props.day.format("YYYY-MM-DD"))) {
            badgeColor = theme.palette.holiday.national;
        } else if (highlightedHoliday) {
            switch (highlightedHoliday.status) {
                case 0:
                    badgeColor = theme.palette.holiday.pending;
                    break;
                case 1:
                    badgeColor = highlightedHoliday.type === 3
                        ? theme.palette.holiday.sickLeave
                        : theme.palette.holiday.approved;
                    break;
                case 2:
                    badgeColor = theme.palette.holiday.rejected;
                    break;
                default:
                    badgeColor = theme.palette.common.white;
            }
        }

        let holidayTitle;
        if (getHolidayTitle(props.day.format("YYYY-MM-DD"))) {
            holidayTitle = getHolidayTitle(props.day.format("YYYY-MM-DD"));
        }

        const badgeReplacer = (
            <>
                {holidayTitle ? (
                    <Tooltip title={holidayTitle} arrow>
                        <Circle
                            sx={{
                                color: badgeColor,
                                mt: 3.2,
                                mr: 3.6,
                                fontSize: "42px",
                            }}
                        />
                    </Tooltip>
                ) : (
                    <Circle
                        sx={{
                            color: badgeColor,
                            mt: 3.2,
                            mr: 3.6,
                            fontSize: "42px",
                        }}
                    />
                )}
            </>
        );

        return (
            <Badge
                sx={{
                    "& .MuiBadge-badge": {
                        mixBlendMode: "darken",
                    },
                }}
                key={props.day.toString()}
                overlap="circular"
                badgeContent={
                    highlightedHoliday ||
                    isNationalHoliday(props.day.format("YYYY-MM-DD"))
                        ? badgeReplacer
                        : null
                }
            >
                <PickersDay
                    outsideCurrentMonth={outsideCurrentMonth}
                    {...other}
                    day={day}
                />
            </Badge>
        );
    };

    // =========================================

    const isLargeScreen = useMediaQuery("(min-width: 1280px)");
    const itemsPerRow = isLargeScreen ? 3 : 4;

    return (
        <Grid
            container
            sx={{
                maxWidth: "1400px",
                width: "100%",
                margin: "auto",
            }}
        >
            <IconButton
                className="calendar-navigate prev"
                color="primary"
                onClick={previousYear}
            >
                <ChevronLeftIcon />
            </IconButton>
            <IconButton className="calendar-navigate next" onClick={nextYear}>
                <ChevronRightIcon />
            </IconButton>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
                {monthsData.map((month) => (
                    <Grid
                        item
                        xs={12}
                        md={6}
                        lg={itemsPerRow}
                        xl={itemsPerRow}
                        key={`${month.month}_${month.year}`}
                    >
                        <DateCalendar
                            value={month.initDate}
                            key={month.month}
                            defaultValue={month.initDate}
                            views={["day"]}
                            sx={{
                                width: "230px",
                                "& .MuiPickersDay-root": {
                                    height: "1.5rem",
                                    width: "1.5rem",
                                },
                                "& .MuiPickersDay-root.Mui-selected": {
                                    backgroundColor: "rgba(0,0,0,0)",
                                    mixBlendMode: "darken",
                                    color: "rgba(0, 0, 0, 0.65)",
                                },
                                "& .MuiPickersDay-root.Mui-selected:hover": {
                                    backgroundColor: "rgba(0,0,0,0)",
                                    mixBlendMode: "darken",
                                    color: "rgba(0, 0, 0, 0.65)",
                                },
                                "& .MuiPickersDay-root:focus.Mui-selected": {
                                    backgroundColor: "rgba(0,0,0,0)",
                                    mixBlendMode: "darken",
                                    color: "rgba(0, 0, 0, 0.65)",
                                },
                                "& .MuiPickersArrowSwitcher-root ": {
                                    display: "none",
                                },
                            }}
                            slots={{
                                day: React.forwardRef((props: any, ref) => (
                                    <Holidays
                                        {...props}
                                        ref={
                                            ref as React.Ref<HTMLButtonElement>
                                        }
                                    />
                                )) as any,
                            }}
                            slotProps={{
                                day: {
                                    highlightedDays,
                                    nationalHolidays,
                                } as unknown as SlotProps,
                            }}
                        />
                    </Grid>
                ))}
            </LocalizationProvider>
        </Grid>
    );
};

export default MonthCalendarCustom;
